// ============================================================================
// Contracts data layer (Phase 1, 2026-05-24).
// Tables: contracts, contract_line_items. Triggers assign IDs + numbers.
// Approval transitions go through SECURITY DEFINER RPCs:
//   submit_contract_for_finance, approve_contract_finance,
//   approve_contract_legal, approve_contract_executive, reject_contract.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type {
  ContractFacilityRow,
  ContractLineItemRow,
  ContractLite,
  ContractRow,
  ContractStatus,
  NewContractInput,
  NewLineItemInput,
  UpdateContractInput,
  UpdateLineItemInput,
} from '../types';
import { errorMessage } from '../../../shared/errors';

const CONTRACT_SELECT = `
  contract_id, contract_number, account_id, facility_id,
  source_bid_id, source_opportunity_id,
  contract_type, billing_model,
  monthly_rate, hourly_rate, estimated_monthly_hours,
  start_date, end_date,
  scope_summary, scope_of_work, payment_terms, notes,
  status, created_by_eid,
  document_hash, hash_algo, certificate_url, sealed_pdf_url,
  signature_method, signer_name, signer_email, signer_title,
  finance_approved_by_eid, finance_approved_at, finance_approval_notes,
  legal_approved_by_eid, legal_approved_at, legal_approval_notes,
  executive_approved_by_eid, executive_approved_at, executive_approval_notes,
  signed_at, document_url,
  rejection_reason, rejected_at, rejected_by_eid,
  parent_contract_id, authored_from_template_id, authored_pdf_generated_at,
  created_at, updated_at,
  accounts!contracts_account_id_fkey ( account_name ),
  facilities ( facility_name, market_region ),
  creator:employees!contracts_created_by_eid_fkey (
    employee_name_first, employee_name_last
  )
`;

// --------------------------- LIST ------------------------------------------

interface ContractsFilter {
  statuses?: ContractStatus[];
  createdBy?: string;
  facilityId?: string;
  accountId?: string;
}

export interface ContractsResult {
  data: ContractRow[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
}

export function useContracts(filter: ContractsFilter = {}): ContractsResult {
  const [data, setData] = useState<ContractRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const filterKey = JSON.stringify({
    statuses: filter.statuses ?? null,
    createdBy: filter.createdBy ?? null,
    facilityId: filter.facilityId ?? null,
    accountId: filter.accountId ?? null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading((prev) => (lastUpdated === null ? true : prev));
      setError(null);
      try {
        let q = supabase.from('contracts').select(CONTRACT_SELECT);
        if (filter.statuses && filter.statuses.length > 0) {
          q = q.in('status', filter.statuses);
        }
        if (filter.createdBy) q = q.eq('created_by_eid', filter.createdBy);
        if (filter.facilityId) q = q.eq('facility_id', filter.facilityId);
        if (filter.accountId) q = q.eq('account_id', filter.accountId);
        const { data: rows, error: e } = await q.order('created_at', {
          ascending: false,
          nullsFirst: false,
        });
        if (cancelled) return;
        if (e) throw e;
        const list = (rows ?? []) as unknown as ContractRow[];
        // Enrich account-wide contracts with covered-facility counts
        // (flat IN-lookup, client-side join — standard pattern).
        const accountWideIds = list
          .filter((c) => !c.facility_id)
          .map((c) => c.contract_id);
        if (accountWideIds.length > 0) {
          const { data: cfRows } = await supabase
            .from('contract_facilities')
            .select('contract_id')
            .in('contract_id', accountWideIds);
          if (cfRows) {
            const counts = new Map<string, number>();
            for (const r of cfRows as Array<{ contract_id: string }>) {
              counts.set(r.contract_id, (counts.get(r.contract_id) ?? 0) + 1);
            }
            for (const c of list) {
              if (!c.facility_id) {
                c.covered_facility_count = counts.get(c.contract_id) ?? 0;
              }
            }
          }
        }
        if (cancelled) return;
        setData(list);
        setLastUpdated(new Date());
      } catch (err) {
        if (cancelled) return;
        console.error('[contracts] load failed', err);
        setError(errorMessage(err, 'Failed to load contracts.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, filterKey]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return useMemo(
    () => ({ data, isLoading, error, lastUpdated, refetch }),
    [data, isLoading, error, lastUpdated, refetch],
  );
}

// --------------------------- ONE -------------------------------------------

export function useContract(contractId: string | null) {
  const [data, setData] = useState<ContractRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!contractId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: row, error: e } = await supabase
          .from('contracts')
          .select(CONTRACT_SELECT)
          .eq('contract_id', contractId)
          .maybeSingle();
        if (cancelled) return;
        if (e) throw e;
        setData((row as unknown as ContractRow) ?? null);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load the contract.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId, tick]);

  return { data, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

// --------------------------- LINE ITEMS ------------------------------------

export function useLineItems(contractId: string | null) {
  const [data, setData] = useState<ContractLineItemRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!contractId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: rows, error: e } = await supabase
          .from('contract_line_items')
          .select('*')
          .eq('contract_id', contractId)
          .order('created_at', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        setData((rows ?? []) as unknown as ContractLineItemRow[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load line items.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { data, isLoading, error, refetch };
}

// --------------------------- MUTATIONS -------------------------------------

export async function createContract(
  input: NewContractInput,
  createdByEid: string,
): Promise<{ contract_id: string } | null> {
  const payload: Record<string, unknown> = {
    account_id: input.account_id,
    facility_id: input.facility_id,
    source_bid_id: input.source_bid_id,
    source_opportunity_id: input.source_opportunity_id,
    contract_type: input.contract_type,
    billing_model: input.billing_model,
    monthly_rate: input.monthly_rate,
    hourly_rate: input.hourly_rate,
    estimated_monthly_hours: input.estimated_monthly_hours,
    start_date: input.start_date,
    end_date: input.end_date,
    scope_summary: input.scope_summary,
    scope_of_work: input.scope_of_work,
    payment_terms: input.payment_terms ?? 'Net 30',
    notes: input.notes,
    status: 'Draft',
    created_by_eid: createdByEid,
    parent_contract_id: input.parent_contract_id ?? null,
  };
  const { data, error } = await supabase
    .from('contracts')
    .insert(payload)
    .select('contract_id')
    .single();
  if (error) throw error;
  return (data as { contract_id: string } | null) ?? null;
}

export async function updateContract(
  contractId: string,
  patch: UpdateContractInput,
): Promise<void> {
  const { error } = await supabase
    .from('contracts')
    .update(patch)
    .eq('contract_id', contractId);
  if (error) throw error;
}

export async function markContractStatus(
  contractId: string,
  status: ContractStatus,
): Promise<void> {
  const payload: Record<string, unknown> = { status };
  if (status === 'Signed') {
    payload.signed_at = new Date().toISOString();
  }
  const { error } = await supabase
    .from('contracts')
    .update(payload)
    .eq('contract_id', contractId);
  if (error) throw error;
}

// --------- Approval RPCs ---------------------------------------------------

export async function submitForFinance(contractId: string): Promise<void> {
  const { error } = await supabase.rpc('submit_contract_for_finance', {
    p_contract_id: contractId,
  });
  if (error) throw error;
}

export async function approveFinance(
  contractId: string,
  notes: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('approve_contract_finance', {
    p_contract_id: contractId,
    p_notes: notes,
  });
  if (error) throw error;
}

export async function approveLegal(
  contractId: string,
  notes: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('approve_contract_legal', {
    p_contract_id: contractId,
    p_notes: notes,
  });
  if (error) throw error;
}

export async function approveExecutive(
  contractId: string,
  notes: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('approve_contract_executive', {
    p_contract_id: contractId,
    p_notes: notes,
  });
  if (error) throw error;
}

export async function rejectContract(
  contractId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc('reject_contract', {
    p_contract_id: contractId,
    p_reason: reason,
  });
  if (error) throw error;
}

// Hard-delete a Draft contract. RPC enforces status=Draft + creator/superuser.
// Submitted contracts must use Cancelled / Rejected instead.
export async function deleteContractDraft(contractId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_contract_draft', {
    p_contract_id: contractId,
  });
  if (error) throw error;
}

// --------- Line item mutations --------------------------------------------

export async function createLineItem(input: NewLineItemInput): Promise<void> {
  // Compute margin_pct server-side if both line_total and sub_cost are set.
  let margin_pct: number | null = null;
  if (
    input.line_total != null &&
    input.line_total > 0 &&
    input.sub_cost != null
  ) {
    margin_pct = Number(
      (((input.line_total - input.sub_cost) / input.line_total) * 100).toFixed(2),
    );
  }
  const payload: Record<string, unknown> = {
    contract_id: input.contract_id,
    description: input.description,
    line_type: input.line_type,
    quantity: input.quantity,
    unit_label: input.unit_label,
    unit_rate: input.unit_rate,
    line_total: input.line_total,
    sub_cost: input.sub_cost,
    margin_pct,
    notes: input.notes,
  };
  const { error } = await supabase.from('contract_line_items').insert(payload);
  if (error) throw error;
}

export async function updateLineItem(
  lineId: string,
  patch: UpdateLineItemInput,
): Promise<void> {
  const payload: Record<string, unknown> = { ...patch };
  if (
    patch.line_total != null &&
    patch.line_total > 0 &&
    patch.sub_cost != null
  ) {
    payload.margin_pct = Number(
      (((patch.line_total - patch.sub_cost) / patch.line_total) * 100).toFixed(2),
    );
  }
  const { error } = await supabase
    .from('contract_line_items')
    .update(payload)
    .eq('line_id', lineId);
  if (error) throw error;
}

export async function deleteLineItem(lineId: string): Promise<void> {
  const { error } = await supabase
    .from('contract_line_items')
    .delete()
    .eq('line_id', lineId);
  if (error) throw error;
}

// --------- Form option hooks ----------------------------------------------

export interface AccountOption {
  account_id: string;
  account_name: string | null;
}

export function useAccountOptions() {
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from('accounts')
          .select('account_id, account_name')
          .order('account_name');
        if (cancelled) return;
        if (e) throw e;
        setAccounts((data ?? []) as AccountOption[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load accounts.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { accounts, isLoading, error };
}

export interface FacilityOptionLite {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  market_region: string | null;
}

// --------- Phase 2: amendment chain ----------------------------------------

/** Children = contracts whose parent_contract_id is this contract. */
export function useContractChildren(contractId: string | null) {
  const [data, setData] = useState<ContractLite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!contractId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const { data: rows, error } = await supabase
          .from('contracts')
          .select('contract_id, contract_number, contract_type, status, start_date')
          .eq('parent_contract_id', contractId)
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (error) throw error;
        setData((rows ?? []) as unknown as ContractLite[]);
      } catch {
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId]);
  return { data, isLoading };
}

/** Parent-picker options: other contracts on the same account. */
export function useParentContractOptions(
  accountId: string | null,
  excludeContractId?: string | null,
) {
  const [data, setData] = useState<ContractLite[]>([]);
  useEffect(() => {
    if (!accountId) {
      setData([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: rows, error } = await supabase
          .from('contracts')
          .select('contract_id, contract_number, contract_type, status, start_date')
          .eq('account_id', accountId)
          .order('created_at', { ascending: false })
          .limit(100);
        if (cancelled) return;
        if (error) throw error;
        const list = ((rows ?? []) as unknown as ContractLite[]).filter(
          (c) => c.contract_id !== excludeContractId,
        );
        setData(list);
      } catch {
        if (!cancelled) setData([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, excludeContractId]);
  return data;
}

export async function setParentContract(
  contractId: string,
  parentContractId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('contracts')
    .update({ parent_contract_id: parentContractId })
    .eq('contract_id', contractId);
  if (error) throw error;
}

// --------- Phase 2: multi-facility junction --------------------------------

export function useContractFacilities(contractId: string | null) {
  const [data, setData] = useState<ContractFacilityRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!contractId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: rows, error: e } = await supabase
          .from('contract_facilities')
          .select(
            'contract_facility_id, contract_id, facility_id, monthly_rate, hourly_rate, notes, created_at, facilities ( facility_name )',
          )
          .eq('contract_id', contractId)
          .order('created_at', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        setData((rows ?? []) as unknown as ContractFacilityRow[]);
      } catch (err) {
        if (cancelled) return;
        setError(
          errorMessage(err, 'Failed to load covered facilities.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { data, isLoading, error, refetch };
}

export async function addContractFacility(input: {
  contract_id: string;
  facility_id: string;
  monthly_rate: number | null;
  hourly_rate: number | null;
}): Promise<void> {
  const { error } = await supabase.from('contract_facilities').insert(input);
  if (error) throw error;
}

export async function updateContractFacility(
  contractFacilityId: string,
  patch: { monthly_rate?: number | null; hourly_rate?: number | null; notes?: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('contract_facilities')
    .update(patch)
    .eq('contract_facility_id', contractFacilityId);
  if (error) throw error;
}

export async function removeContractFacility(
  contractFacilityId: string,
): Promise<void> {
  const { error } = await supabase
    .from('contract_facilities')
    .delete()
    .eq('contract_facility_id', contractFacilityId);
  if (error) throw error;
}

// --------- Form option hooks (cont.) ----------------------------------------

export function useFacilityOptions() {
  const [facilities, setFacilities] = useState<FacilityOptionLite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from('active_facilities')
          .select('facility_id, facility_name, account_id, market_region')
          .order('facility_name');
        if (cancelled) return;
        if (e) throw e;
        setFacilities((data ?? []) as FacilityOptionLite[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load facilities.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { facilities, isLoading, error };
}
